import React, { useCallback } from 'react';

import styled from 'styled-components';

import { FileModel } from 'models';

import { NotificationsService } from 'core/notifications';
import { Window } from 'core/ui';

import { useApiClient } from 'features/api';

import { FileViewerPage } from './FileViewerPage';

interface FileViewerPopupProps {
  isOpen: boolean;
  files: FileModel[];
  selectedFile: FileModel | null;
  title?: string;
  examId?: number; // Add examId parameter to support direct file fetching
  onClose: () => void;
  onFileDeleted?: (fileId: number) => void;
}

export const FileViewerPopup: React.FC<FileViewerPopupProps> = ({ isOpen, files, selectedFile, title, examId, onClose, onFileDeleted }) => {
  const apiClient = useApiClient();

  const handleFileDeleted = useCallback(
    async (fileId: string | number) => {
      try {
        await apiClient.filesClient.deleteFile(typeof fileId === 'string' ? parseInt(fileId, 10) : fileId, 'msal-required');
        NotificationsService.displaySuccess('File deleted successfully');

        // Notify parent component if needed
        if (onFileDeleted) {
          onFileDeleted(Number(fileId));
        }

        return true;
      } catch (error) {
        console.error('Error deleting file:', error);
        NotificationsService.displayError('Failed to delete file');
        return false;
      }
    },
    [apiClient, onFileDeleted],
  );

  const handleFileEdited = useCallback(
    async (fileId: string | number, newFileName: string) => {
      try {
        await apiClient.filesClient.renameFile(Number(fileId), [{ op: 'replace', path: '/fileName', value: newFileName }]);
        NotificationsService.displaySuccess('File renamed successfully');
        return true;
      } catch (error) {
        console.error('Error renaming file:', error);
        NotificationsService.displayError('Failed to rename file');
        return false;
      }
    },
    [apiClient],
  );

  if (!isOpen) return null;

  return (
    <Window title={title || selectedFile?.fileName || 'File Viewer'} initialWidth={1000} initialHeight={1000} modal onClose={onClose}>
      <StyledFileViewerContainer>
        <StyledFileViewerPage
          files={files}
          selectedFile={selectedFile}
          examId={examId}
          showDeleteBtn
          showEditBtn
          showInfobar
          showSidebar
          onDeleteFile={handleFileDeleted}
          onFileDeleted={(fileId) => {
            if (onFileDeleted) {
              onFileDeleted(Number(fileId));
            }
          }}
        />
      </StyledFileViewerContainer>
    </Window>
  );
};

const StyledFileViewerContainer = styled.div`
  height: 100%;
`;

const StyledFileViewerPage = styled(FileViewerPage)`
  width: 100%;
  height: 100%;
`;
